import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Icon, Typo, theme } from '../../../ui';
import { toRgba } from '../../../ui/theme/colors';
import { MODULE_CAPABILITIES, MODULE_TYPES } from '../moduleConfig';

const ModuleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 16px;
  padding: 8px 16px 16px;
  width: 100%;
`;

const ModuleTypeCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px 12px;
  border: 1px solid ${({ theme }) => theme.colors.NEU_200};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.NEU_0};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.PRIMARY_MEDIUM};
    background: ${({ theme }) => theme.colors.PRIMARY_EXTRA_LIGHT};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${toRgba(theme.colors.NEU_999, 0.1)};
  }

  &:active {
    transform: translateY(0);
  }
`;

/**
 * ModuleTypeSelector - Grid of module type cards for selection
 *
 * @param {Object} props
 * @param {Function} props.onSelectModuleType - Callback when a module type is selected
 * @param {string[]} props.allowedModuleTypes - Optional array of module types to allow
 */
const ModuleTypeSelector = ({ onSelectModuleType, allowedModuleTypes }) => {
  const { t } = useTranslation();
  const filteredModuleTypes = useMemo(() => {
    return MODULE_TYPES.filter(({ type }) => {
      const capabilities = MODULE_CAPABILITIES[type];
      if (!capabilities?.hasContent && !capabilities?.hasSettings) {
        return false;
      }
      if (allowedModuleTypes && allowedModuleTypes.length > 0) {
        return allowedModuleTypes.includes(type);
      }
      return true;
    });
  }, [allowedModuleTypes]);

  return (
    <ModuleGrid>
      {filteredModuleTypes.map(({ type, icon, labelKey }) => {
        const IconComponent = Icon[icon];
        const translatedLabel = t(labelKey);
        return (
          <ModuleTypeCard key={type} onClick={() => onSelectModuleType(type)} aria-label={t('editCourse.addModuleAriaLabel', { moduleType: translatedLabel })}>
            {IconComponent && <IconComponent size='medium' color={theme.colors.NEU_PRIMARY} />}
            <Typo.Info $caps>{translatedLabel}</Typo.Info>
          </ModuleTypeCard>
        );
      })}
    </ModuleGrid>
  );
};

export default ModuleTypeSelector;
